/**
 * WonderPush Web SDK integration (ADR-0019 / ISSUE-065).
 *
 * Replaces the first-party VAPID web-push (ISSUE-035). The SDK is lazy-loaded —
 * the loader script is only injected the first time we actually need push — and
 * starts in consent mode (`setRequiresUserConsent(true)`), so nothing leaves the
 * browser until our existing ≥3-logs prompt flow grants consent (ADR-0009 §8).
 * Its service worker replaces the VAPID worker.
 *
 * Lifecycle:
 *   - ensureWonderPushLoaded()  — inject + init (idempotent, no consent yet).
 *   - setWonderPushUserId(id)   — bind the device to our userId after auth.
 *   - clearWonderPushUserId()   — unbind on sign-out.
 *   - grantPushConsent()        — called ONLY by the ≥3-logs prompt: flips
 *                                 consent on, subscribes, and reports the
 *                                 subscription marker to the API.
 *   - revokePushConsent()       — user turns push off.
 *
 * `VITE_WONDERPUSH_WEB_KEY` empty ⇒ disabled (isWonderPushEnabled() is false and
 * every call is a no-op), so dev/test builds without a key stay inert.
 */
import { api } from '../api.js';

/**
 * WonderPush web SDK command-queue (loader pattern). Before the loader boots it
 * is a plain array of buffered commands; the loader then replaces `.push` with
 * its command processor. Either way `WonderPush.push([...])` is the call shape.
 */
declare global {
  interface Window {
    WonderPush?: unknown[];
  }
}

const env = import.meta.env as unknown as Record<string, string | undefined>;
const WEB_KEY = env.VITE_WONDERPUSH_WEB_KEY;
const LOADER_SRC = 'https://cdn.by.wonderpush.com/sdk/1.1/wonderpush-loader.min.js';

let loadPromise: Promise<void> | null = null;

export function isWonderPushEnabled(): boolean {
  return typeof window !== 'undefined' && !!WEB_KEY;
}

export function isNotificationPermissionSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationPermissionSupported()) return 'unsupported';
  return window.Notification.permission;
}

export async function requestBrowserNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationPermissionSupported()) return 'denied';
  return window.Notification.requestPermission();
}

/** Push a command onto the WonderPush queue (created lazily). */
function wp(...command: unknown[]): void {
  if (!isWonderPushEnabled()) return;
  window.WonderPush = window.WonderPush ?? [];
  // The real loader replaces this array's push with its command processor.
  window.WonderPush.push(command);
}

/**
 * Inject the loader and initialise the SDK in consent mode. Idempotent — the
 * loader is added at most once per page. Resolves once the script tag is in the
 * DOM (the SDK's own queue buffers commands until it finishes booting).
 */
export function ensureWonderPushLoaded(): Promise<void> {
  if (!isWonderPushEnabled()) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // Seed the command queue before the loader arrives so init/setUserId calls
    // made during boot are buffered and replayed.
    window.WonderPush = window.WonderPush ?? [];
    // `requiresUserConsent: true` keeps the SDK inert until setUserConsent(true).
    wp('init', { webKey: WEB_KEY, requiresUserConsent: true });

    if (document.querySelector(`script[src="${LOADER_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = LOADER_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('WonderPush loader failed to load'));
    document.head.appendChild(script);
  });
  return loadPromise;
}

/** Bind the subscribed device to our opaque userId (call after auth). */
export async function setWonderPushUserId(userId: string): Promise<void> {
  if (!isWonderPushEnabled()) return;
  await ensureWonderPushLoaded();
  wp('setUserId', userId);
}

/** Unbind the userId on sign-out so the next user starts clean. */
export function clearWonderPushUserId(): void {
  if (!isWonderPushEnabled()) return;
  wp('setUserId', null);
}

/**
 * Grant push consent — invoked exclusively by the ≥3-logs prompt flow.
 * Flips WonderPush consent on, subscribes to notifications, and records the
 * userId-bound subscription marker server-side so the dispatcher knows the user
 * has a healthy push surface (and won't fall back to email).
 */
export async function grantPushConsent(): Promise<void> {
  if (!isWonderPushEnabled()) return;
  await ensureWonderPushLoaded();
  wp('setUserConsent', true);
  wp('subscribeToNotifications');
  await reportSubscription(true, { strict: true });
}

/** Revoke push consent (user turns push off). */
export async function revokePushConsent(): Promise<void> {
  if (!isWonderPushEnabled()) return;
  wp('unsubscribeFromNotifications');
  wp('setUserConsent', false);
  await reportSubscription(false);
}

/** POST the subscription marker; non-fatal on failure (best-effort sync). */
async function reportSubscription(
  subscribed: boolean,
  options: { strict: boolean } = { strict: false },
): Promise<void> {
  try {
    const { error } = await api.POST('/me/wonderpush/subscription', { body: { subscribed } });
    if (error) throw new Error('WonderPush marker sync failed');
  } catch {
    if (options.strict) throw new Error('WonderPush marker sync failed');
    // Marker is a hint for the email-fallback decision; safe to retry later.
  }
}
